/** Combining catalogue products into one tour: the run's choices, as pure state.
 *
 *  A product sync builds one tour per selected product. A JOIN says "do not
 *  build B; make B's IDs additional IDs of the tour A creates" (plan
 *  2026-10-11-product-sync-join-products-into-one-tour, §1). A is the group's
 *  PRIMARY, B a MEMBER. Members stay in `product_ids`, so the run's label and
 *  booking backfill still cover them.
 *
 *  Besides the joins, the run carries the operator's consents:
 *  - `moveFromTourIds` (D4): the tours a member's IDs may be taken from;
 *  - `moveBookings` + `bookingMove` (D5): whether a moved member's future
 *    bookings follow it, and the run-wide answers the Edit-booking task needs;
 *  - `splitOff` (D2): a built product whose key is an additional ID of another
 *    tour gets its own tour instead of being left out.
 *
 *  Everything here is pure and works on the listing already in hand, so the
 *  form re-answers on every click. The server re-validates every rule at start
 *  and stays authoritative: `joinProblems` only mirrors the J-rules the listing
 *  can decide (J2, J4, J5, J6, J8, and J10's missing answers).
 */
import type {
  BookingMoveChoice,
  JoinStartFields,
  JoinStartItem,
  MappingPolicy,
  ProductListItem,
  ProductMapping,
} from '@/api/sync';

import type { MintedMapping } from './mappingPolicy';
import { mintForProduct, policyStatus } from './mappingPolicy';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** The start-payload types live with the API wrappers (`@/api/sync`), which
 *  `SyncProductsStartInput` and the preview share; re-exported for callers of
 *  this module. `BookingMoveChoice` holds the Edit-booking task's two
 *  questions, answered once for the run (D5), with no defaults. */
export type { BookingMoveChoice, JoinStartFields, JoinStartItem, SplitOffStartItem } from '@/api/sync';

export interface JoinState {
  /** member product id -> the primary product id it joins. */
  readonly joins: ReadonlyMap<string, string>;
  /** member -> the tours the operator agreed to take its IDs from (D4). Absent
   *  means no consent: the server refuses a member whose ID another tour owns. */
  readonly moveFromTourIds: ReadonlyMap<string, readonly number[]>;
  /** member -> also move its future bookings off the old tour (D5). Only
   *  `true` entries of members with a move consent are kept. */
  readonly moveBookings: ReadonlyMap<string, boolean>;
  /** The run-wide answers, needed once any member moves bookings. Reset to
   *  null when none does, so a later tick asks again rather than reusing them. */
  readonly bookingMove: BookingMoveChoice | null;
  /** built product id -> the tour it is split off (D2, §4a). */
  readonly splitOff: ReadonlyMap<string, number>;
}

/** The empty state: no joins, no consents. Also the initial state. */
export function clearAll(): JoinState {
  return {
    joins: new Map(),
    moveFromTourIds: new Map(),
    moveBookings: new Map(),
    bookingMove: null,
    splitOff: new Map(),
  };
}

/** Drop every choice that no longer applies: consents of products that are no
 *  longer members, booking moves of members that move no ID (the bookings to
 *  move sit on a tour the member's IDs are taken from), split-offs of members
 *  (a member is not built), and the run-wide booking answers once no member
 *  moves bookings. */
function normalize(s: JoinState): JoinState {
  const moveFromTourIds = new Map(
    [...s.moveFromTourIds].filter(([id, tours]) => s.joins.has(id) && tours.length > 0),
  );
  const moveBookings = new Map(
    [...s.moveBookings].filter(([id, on]) => on && moveFromTourIds.has(id)),
  );
  const splitOff = new Map([...s.splitOff].filter(([id]) => !s.joins.has(id)));
  return {
    joins: s.joins,
    moveFromTourIds,
    moveBookings,
    bookingMove: moveBookings.size > 0 ? s.bookingMove : null,
    splitOff,
  };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** The members joining `primaryId`, in the order they were joined. */
export function membersOf(state: JoinState, primaryId: string): string[] {
  return [...state.joins].filter(([, p]) => p === primaryId).map(([m]) => m);
}

/** How `productId` takes part in the run's joins. */
export function roleOf(state: JoinState, productId: string): 'primary' | 'member' | null {
  if (state.joins.has(productId)) return 'member';
  for (const p of state.joins.values()) if (p === productId) return 'primary';
  return null;
}

// ---------------------------------------------------------------------------
// Mutators: each returns a new state and never changes its input
// ---------------------------------------------------------------------------

/** Join every product of `productIds` other than `primaryId` into `primaryId`.
 *
 *  - The primary leaves any group it was a member of: it now creates a tour.
 *  - A product that was itself a group's primary brings its members along, so
 *    the result never has a chain: they were going to be one tour with it.
 *  - A member keeps its move consents (they name the tours owning its IDs,
 *    whatever it joins) and loses any split-off (a member is not built).
 */
export function combine(
  state: JoinState,
  productIds: Iterable<string>,
  primaryId: string,
): JoinState {
  const members = [...new Set(productIds)].filter((id) => id !== primaryId);
  if (members.length === 0) return state;
  const joins = new Map(state.joins);
  joins.delete(primaryId);
  for (const id of members) {
    for (const [m, p] of joins) if (p === id) joins.set(m, primaryId);
    joins.set(id, primaryId);
  }
  return normalize({ ...state, joins });
}

/** Take `memberId` out of its group: it is built on its own again. */
export function detach(state: JoinState, memberId: string): JoinState {
  if (!state.joins.has(memberId)) return state;
  const joins = new Map(state.joins);
  joins.delete(memberId);
  return normalize({ ...state, joins });
}

/** The product left the run's selection. A member leaves its group; a primary's
 *  members are detached (they stay selected and are built on their own); a
 *  split-off is dropped. */
export function onDeselect(state: JoinState, productId: string): JoinState {
  const isPrimary = roleOf(state, productId) === 'primary';
  if (!state.joins.has(productId) && !isPrimary && !state.splitOff.has(productId)) return state;
  const joins = new Map([...state.joins].filter(([m, p]) => m !== productId && p !== productId));
  const splitOff = new Map(state.splitOff);
  splitOff.delete(productId);
  return normalize({ ...state, joins, splitOff });
}

/** `onDeselect` for every product the state mentions that `selected` lacks:
 *  for selection changes that replace the whole set (select all / none). */
export function retainSelected(state: JoinState, selected: ReadonlySet<string>): JoinState {
  const mentioned = new Set([
    ...state.joins.keys(),
    ...state.joins.values(),
    ...state.splitOff.keys(),
  ]);
  let out = state;
  for (const id of mentioned) if (!selected.has(id)) out = onDeselect(out, id);
  return out;
}

/** Record which tours the operator agreed to take `memberId`'s IDs from (D4);
 *  null or [] withdraws the consent. Ignored for a product that is no member. */
export function setMoveConsent(
  state: JoinState,
  memberId: string,
  tourIds: readonly number[] | null,
): JoinState {
  if (!state.joins.has(memberId)) return state;
  const moveFromTourIds = new Map(state.moveFromTourIds);
  const tours = [...new Set(tourIds ?? [])].sort((a, b) => a - b);
  if (tours.length) moveFromTourIds.set(memberId, tours);
  else moveFromTourIds.delete(memberId);
  return normalize({ ...state, moveFromTourIds });
}

/** Tick or untick "also move this member's future bookings" (D5). Ignored for
 *  a product that is no member or has no move consent; withdrawing the consent
 *  unticks it. */
export function setMoveBookings(state: JoinState, memberId: string, on: boolean): JoinState {
  if (!state.moveFromTourIds.has(memberId)) return state;
  const moveBookings = new Map(state.moveBookings);
  if (on) moveBookings.set(memberId, true);
  else moveBookings.delete(memberId);
  return normalize({ ...state, moveBookings });
}

/** Answer (or clear) the run-wide booking-move questions. Kept only while some
 *  member moves bookings. */
export function setBookingMove(state: JoinState, choice: BookingMoveChoice | null): JoinState {
  return normalize({ ...state, bookingMove: choice ? { ...choice } : null });
}

/** Split `productId` off `fromTourId` and build its own tour (D2), or undo it
 *  with null. Ignored for a member, which is not built. */
export function setSplitOff(
  state: JoinState,
  productId: string,
  fromTourId: number | null,
): JoinState {
  if (state.joins.has(productId)) return state;
  const splitOff = new Map(state.splitOff);
  if (fromTourId === null) splitOff.delete(productId);
  else splitOff.set(productId, fromTourId);
  return normalize({ ...state, splitOff });
}

// ---------------------------------------------------------------------------
// Start payload (plan §1)
// ---------------------------------------------------------------------------

/** The state as start fields. All three are always present: `joins` and
 *  `split_off` as arrays, `[]` included, and `booking_move` as null unless a
 *  member moves bookings and both answers are given. The setup wizard
 *  deep-merges a staged payload, so an omitted key would keep the previous
 *  run's value (plan Fact 7). */
export function toStartPayload(state: JoinState): JoinStartFields {
  const joins = [...state.joins].map(([member, primary]) => {
    const item: JoinStartItem = { product_id: member, into_product_id: primary };
    const tours = state.moveFromTourIds.get(member);
    if (tours?.length) item.move_from_tour_ids = [...tours];
    if (state.moveBookings.get(member)) item.move_bookings = true;
    return item;
  });
  const split_off = [...state.splitOff].map(([product_id, from_tour_id]) => ({
    product_id,
    from_tour_id,
  }));
  const booking_move =
    state.bookingMove && joins.some((j) => j.move_bookings) ? { ...state.bookingMove } : null;
  return { joins, split_off, booking_move };
}

// ---------------------------------------------------------------------------
// Ownership: who holds each value a product mints
// ---------------------------------------------------------------------------

export interface ValueOwner {
  value: string;
  optionId: string | null;
  /** `none`: no tour holds the value. `primary`: it is a tour's own ID
   *  (`via: "custom_id"`), usually the product's own tour from an earlier sync.
   *  `alias`: it is an additional ID of a tour (`via: "ext_id"`). */
  kind: 'none' | 'primary' | 'alias';
  tour: ProductMapping['tour'] | null;
  /** An alias with `use_for_matching: false`: it resolves nothing, but still
   *  holds the value, so moving the ID still takes it from that tour. */
  parked: boolean;
  /** The listing row that holds it. */
  mapping: ProductMapping | null;
}

function mintedFor(policy: MappingPolicy | undefined, product: ProductListItem): MintedMapping[] {
  return policy?.mintable ? mintForProduct(policy, product) : [];
}

/** Who holds each value `policy` mints for `product`. When several rows hold a
 *  value, the one that resolves it wins, as the server's `owners()` decides: an
 *  alias used for matching, then a tour's own ID, then a parked alias. */
export function ownership(
  product: ProductListItem,
  mappings: ProductMapping[] | undefined,
  policy: MappingPolicy | undefined,
): ValueOwner[] {
  const present = mappings ?? [];
  return mintedFor(policy, product).map(({ value, optionId }): ValueOwner => {
    const holders = present.filter((m) => m.custom_id === value);
    const holder =
      holders.find((m) => m.via === 'ext_id' && m.use_for_matching !== false) ??
      holders.find((m) => m.via === 'custom_id') ??
      holders.find((m) => m.via === 'ext_id');
    if (!holder) {
      return { value, optionId, kind: 'none', tour: null, parked: false, mapping: null };
    }
    return {
      value,
      optionId,
      kind: holder.via === 'custom_id' ? 'primary' : 'alias',
      tour: holder.tour,
      parked: holder.via === 'ext_id' && holder.use_for_matching === false,
      mapping: holder,
    };
  });
}

// ---------------------------------------------------------------------------
// Rules the listing can decide
// ---------------------------------------------------------------------------

/** What the rules read: the listing and the run's selection and policy. */
export interface JoinContext {
  products: readonly ProductListItem[];
  /** The listing's `mappings`, keyed by catalogue product id. */
  mappings: Readonly<Record<string, ProductMapping[] | undefined>>;
  policy: MappingPolicy | undefined;
  /** The run's `product_ids`. */
  selected: ReadonlySet<string>;
  /** The integration's builder resolves a key that is another tour's
   *  additional ID to that tour's own key and builds (updates) that tour
   *  (`product_alias_resolution`, Juniper: one hotel under several codes).
   *  Such a product is built, never left out by the re-sync guard (D2). */
  resolvesAliasedKeys: boolean;
  /** The integration can split a product off the tour its key is an
   *  additional ID of (`product_split_off`; not Juniper). J8 offers the
   *  split-off only then, as the server's wording does. */
  canSplitOff: boolean;
}

export type JoinProblemCode =
  /** J2: the member is not in the run's selection. */
  | 'not_selected'
  /** J2: the product it joins is not in the run's selection. */
  | 'target_not_selected'
  /** J2: joined into itself. */
  | 'self_join'
  /** J2: joined into a product that itself joins another (no chains). */
  | 'chain'
  /** J4: the member mints no ID under the run's policy. */
  | 'mints_nothing'
  /** J5: the primary would create no tour or several. */
  | 'primary_not_single'
  /** J6: another tour holds a member's ID and the operator has not agreed to
   *  take it (move) or left the member out. */
  | 'owned_elsewhere'
  /** J8: the primary's key is an additional ID of another tour and it is not
   *  split off that tour. */
  | 'primary_mapped_elsewhere'
  /** J10: a member moves bookings but the run-wide questions are unanswered. */
  | 'booking_move_unanswered';

export interface JoinProblem {
  productId: string;
  rule: 'J2' | 'J4' | 'J5' | 'J6' | 'J8' | 'J10';
  code: JoinProblemCode;
  /** One sentence for the operator, naming the product. */
  detail: string;
  /** `owned_elsewhere`: the holders still lacking consent. Their tour ids are
   *  what a "move" adds to `moveFromTourIds`. */
  owners?: ValueOwner[];
  /** `primary_mapped_elsewhere`: the tours the key resolves to; split-off
   *  offers the one tour as `from_tour_id`. */
  tours?: ProductMapping['tour'][];
}

function tourLabel(t: ProductMapping['tour']): string {
  return t.name ? `${t.name} (#${t.id})` : `#${t.id}`;
}

function toursLabel(tours: ProductMapping['tour'][]): string {
  const labels = tours.map(tourLabel);
  if (labels.length <= 1) return `tour ${labels[0] ?? ''}`;
  return `tours ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function uniqueTours(tours: ProductMapping['tour'][]): ProductMapping['tour'][] {
  const seen = new Map<number, ProductMapping['tour']>();
  for (const t of tours) if (!seen.has(t.id)) seen.set(t.id, t);
  return [...seen.values()];
}

function indexProducts(products: readonly ProductListItem[]): Map<string, ProductListItem> {
  const out = new Map<string, ProductListItem>();
  for (const p of products) if (p.id) out.set(p.id, p);
  return out;
}

export interface KeyGuard {
  /** `free`: the product is built. No other tour holds its key, or the
   *  integration builds that tour itself (`resolvesAliasedKeys`).
   *  `split_off`: another tour does, and the run splits the product off it.
   *  `left_out`: another tour does, and the re-sync guard leaves the product
   *  out (an ungrouped product), or the server refuses it (J8, a primary). */
  verdict: 'free' | 'split_off' | 'left_out';
  /** `policyStatus(...).elsewhere`: the additional IDs holding the key,
   *  whatever the verdict. */
  elsewhere: ProductMapping[];
}

/** The additional IDs of other tours that hold `product`'s key, and whether
 *  the run's split-off covers them all: a split-off names one tour, so it
 *  covers a key held by that tour only. */
function heldElsewhere(
  state: JoinState,
  product: ProductListItem,
  ctx: Pick<JoinContext, 'mappings' | 'policy'>,
): { elsewhere: ProductMapping[]; splitOff: boolean } {
  const elsewhere = policyStatus(ctx.policy, product, mappingsOf(ctx, product.id)).elsewhere;
  const from = product.id ? state.splitOff.get(product.id) : undefined;
  const splitOff =
    elsewhere.length > 0 && from !== undefined && elsewhere.every((m) => m.tour.id === from);
  return { elsewhere, splitOff };
}

/** How the re-sync guard (D2) treats `product` when it is built. */
export function keyGuard(
  state: JoinState,
  product: ProductListItem,
  ctx: Pick<JoinContext, 'mappings' | 'policy' | 'resolvesAliasedKeys'>,
): KeyGuard {
  const { elsewhere, splitOff } = heldElsewhere(state, product, ctx);
  // An integration that resolves the key builds the tour holding it: nothing
  // is overwritten by surprise, so the guard has nothing to leave out.
  if (elsewhere.length === 0 || ctx.resolvesAliasedKeys) return { verdict: 'free', elsewhere };
  return { verdict: splitOff ? 'split_off' : 'left_out', elsewhere };
}

function mappingsOf(
  ctx: Pick<JoinContext, 'mappings'>,
  productId: string | null,
): ProductMapping[] | undefined {
  return productId ? ctx.mappings[productId] : undefined;
}

function targetOf(
  product: ProductListItem,
  ctx: Pick<JoinContext, 'mappings' | 'policy'>,
): number | null {
  const minted = mintedFor(ctx.policy, product);
  if (minted.length !== 1) return null;
  const mappings = mappingsOf(ctx, product.id);
  if (policyStatus(ctx.policy, product, mappings).elsewhere.length) return null;
  const own = (mappings ?? []).find(
    (m) => m.via === 'custom_id' && m.custom_id === minted[0]?.value,
  );
  return own ? own.tour.id : null;
}

/** The existing tour a group joins, or null when the run creates it.
 *
 *  It exists when the primary's one key is already a tour's own ID (the
 *  primary was synced before): the run re-syncs that tour and the members join
 *  it. A split-off primary gets a new tour. A primary whose key resolves to
 *  another tour (J8) has no well-defined target, so null as well.
 */
export function targetTourId(
  primaryId: string,
  ctx: Pick<JoinContext, 'products' | 'mappings' | 'policy'>,
): number | null {
  const product = indexProducts(ctx.products).get(primaryId);
  return product ? targetOf(product, ctx) : null;
}

/** Why `policy` mints nothing for `product`: the server's apply wording, with
 *  the product's name in place of "this product". */
function noMintReason(
  policy: MappingPolicy | undefined,
  product: ProductListItem,
  name: string,
): string {
  if (!policy?.mintable) return 'no creatable mapping policy is selected';
  if (policy.templated) {
    return (product.template_error || `the product template produced no ID for ${name}`).replace(
      /\.$/,
      '',
    );
  }
  return `'${policy.id}' produces no id for ${name} (an option policy needs the product to have options)`;
}

/** Every problem the listing can see in the run's joins, per product id.
 *  Products without a problem are absent; an empty map means none was found.
 *  The server re-checks all of it (and J1, J3, J7, J9, J10's permission) at
 *  start. Ungrouped products are never a problem here: one whose key is an
 *  additional ID elsewhere is simply left out of the build (D2). */
export function joinProblems(state: JoinState, ctx: JoinContext): Map<string, JoinProblem[]> {
  const out = new Map<string, JoinProblem[]>();
  const add = (p: JoinProblem) => out.set(p.productId, [...(out.get(p.productId) ?? []), p]);
  const byId = indexProducts(ctx.products);
  const name = (id: string) => byId.get(id)?.name || id;

  // J2: the shape of the joins themselves.
  for (const [member, primary] of state.joins) {
    if (member === primary) {
      add({
        productId: member,
        rule: 'J2',
        code: 'self_join',
        detail: `${name(member)} cannot join itself.`,
      });
      continue;
    }
    if (!ctx.selected.has(member)) {
      add({
        productId: member,
        rule: 'J2',
        code: 'not_selected',
        detail: `${name(member)} joins ${name(primary)} but is not selected for this run.`,
      });
    }
    if (!ctx.selected.has(primary)) {
      add({
        productId: member,
        rule: 'J2',
        code: 'target_not_selected',
        detail: `${name(member)} joins ${name(primary)}, which is not selected for this run.`,
      });
    }
    const next = state.joins.get(primary);
    if (next !== undefined && next !== primary) {
      add({
        productId: member,
        rule: 'J2',
        code: 'chain',
        detail: `${name(member)} joins ${name(primary)}, which itself joins ${name(next)}: join it into ${name(next)} directly.`,
      });
    }
  }

  // J5, J8: the product that creates each group's tour.
  const primaries = new Set([...state.joins].filter(([m, p]) => m !== p).map(([, p]) => p));
  const targets = new Map<string, number | null>();
  for (const primaryId of primaries) {
    const product = byId.get(primaryId);
    if (!product) continue;
    targets.set(primaryId, targetOf(product, ctx));
    const minted = mintedFor(ctx.policy, product);
    if (minted.length !== 1) {
      const reason = noMintReason(ctx.policy, product, name(primaryId));
      add({
        productId: primaryId,
        rule: 'J5',
        code: 'primary_not_single',
        detail:
          minted.length > 1
            ? `${name(primaryId)} would create ${minted.length} tours; pick one tour to join.`
            : ctx.policy?.mintable
              ? `${name(primaryId)} would create no tour: ${reason}. Pick another product to create the tour.`
              : `${name(primaryId)} would create no tour: ${reason}.`,
      });
    }
    // Not `keyGuard`: an integration that resolves the key would build the
    // tour holding it, not one keyed by the primary, so J8 holds there too.
    const held = heldElsewhere(state, product, ctx);
    if (held.elsewhere.length > 0 && !held.splitOff) {
      const tours = uniqueTours(held.elsewhere.map((m) => m.tour));
      const where = toursLabel(tours);
      const keys = [...new Set(held.elsewhere.map((m) => m.custom_id))];
      // The server's wording (`plan_joins`), with the product's name for its
      // id. A split-off takes the key from ONE tour, and only an integration
      // with `product_split_off` offers it: the run refuses it elsewhere (J9).
      const detail =
        ctx.canSplitOff && tours.length === 1
          ? `${name(primaryId)} maps to ${where} (its ID ${keys.join(', ')} is an additional ID there): split it off that tour, or pick another product to create the tour.`
          : keys.length === 1 && keys[0] === primaryId
            ? `${name(primaryId)} is an additional ID of ${where}: pick another product to create the tour.`
            : keys.length === 1
              ? `${name(primaryId)}'s ID ${keys[0]} is an additional ID of ${where}: pick another product to create the tour.`
              : `${name(primaryId)}'s IDs ${keys.join(', ')} are additional IDs of ${where}: pick another product to create the tour.`;
      add({
        productId: primaryId,
        rule: 'J8',
        code: 'primary_mapped_elsewhere',
        detail,
        tours,
      });
    }
  }

  // J4, J6, J10: each member.
  for (const [member, primary] of state.joins) {
    if (member === primary) continue;
    const product = byId.get(member);
    if (!product) continue;
    const owners = ownership(product, mappingsOf(ctx, member), ctx.policy);
    if (owners.length === 0) {
      add({
        productId: member,
        rule: 'J4',
        code: 'mints_nothing',
        detail: `${name(member)} cannot join: ${noMintReason(ctx.policy, product, name(member))}.`,
      });
    }
    // A value already an additional ID of the tour the group joins is fine
    // (an idempotent re-run). Any other holder needs the operator's choice.
    const target = targets.get(primary) ?? null;
    const consented = new Set(state.moveFromTourIds.get(member) ?? []);
    const unconsented = owners.filter(
      (o) => o.tour !== null && o.tour.id !== target && !consented.has(o.tour.id),
    );
    if (unconsented.length) {
      const tours = uniqueTours(unconsented.flatMap((o) => (o.tour ? [o.tour] : [])));
      add({
        productId: member,
        rule: 'J6',
        code: 'owned_elsewhere',
        detail: `${name(member)} belongs to ${toursLabel(tours)}: choose move or leave out.`,
        owners: unconsented,
      });
    }
    if (state.moveBookings.get(member) && !state.bookingMove) {
      add({
        productId: member,
        rule: 'J10',
        code: 'booking_move_unanswered',
        detail: `Moving the bookings of ${name(member)} needs two answers for the run: whether guests are notified, and what happens to tags only the old tour uses.`,
      });
    }
  }
  return out;
}

/** The product to preselect as "creates the tour" for a new group: the first
 *  one already carrying its own tour (`targetTourId` is that tour), else the
 *  first. */
export function defaultPrimary(
  productIds: readonly string[],
  ctx: Pick<JoinContext, 'products' | 'mappings' | 'policy'>,
): string | undefined {
  const byId = indexProducts(ctx.products);
  const ownsTour = (id: string) => {
    const product = byId.get(id);
    return product !== undefined && targetOf(product, ctx) !== null;
  };
  return productIds.find(ownsTour) ?? productIds[0];
}

// ---------------------------------------------------------------------------
// Launch summary (plan §7)
// ---------------------------------------------------------------------------

/** The counts behind "Builds N products · M join as additional IDs · S split
 *  off · K left out (additional IDs of other tours) · bookings of P products
 *  will be moved". Counts the selected products only. */
export interface LaunchSummary {
  /** Selected products the run builds: everything but members and those left
   *  out. Primaries and split-offs included. */
  built: number;
  /** Selected members: not built; their IDs become additional IDs. */
  joining: number;
  /** Built products split off the tour that holds their key. */
  splitOff: number;
  /** Selected non-members whose key is an additional ID of another tour and
   *  that are not split off it: the re-sync guard leaves them out. Never
   *  counted for an integration that resolves such keys. */
  leftOut: number;
  /** Members whose future bookings move with their IDs. */
  movingBookings: number;
}

export function launchSummary(state: JoinState, ctx: JoinContext): LaunchSummary {
  const byId = indexProducts(ctx.products);
  const summary: LaunchSummary = {
    built: 0,
    joining: 0,
    splitOff: 0,
    leftOut: 0,
    movingBookings: 0,
  };
  for (const id of ctx.selected) {
    if (state.joins.has(id)) {
      summary.joining += 1;
      if (state.moveBookings.get(id)) summary.movingBookings += 1;
      continue;
    }
    const product = byId.get(id);
    const verdict = product ? keyGuard(state, product, ctx).verdict : 'free';
    if (verdict === 'left_out') summary.leftOut += 1;
    else summary.built += 1;
    if (verdict === 'split_off') summary.splitOff += 1;
  }
  return summary;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** The launch panel's line (plan §7): "Builds N products · M join as
 *  additional IDs · S split off · K left out (additional IDs of other tours) ·
 *  bookings of P products will be moved". Parts that are zero are left out;
 *  null when the run neither joins, splits off nor leaves anything out, so a
 *  plain run shows no line at all. */
export function launchSummaryText(s: LaunchSummary): string | null {
  if (s.joining === 0 && s.splitOff === 0 && s.leftOut === 0) return null;
  const parts = [`Builds ${s.built} ${plural(s.built, 'product', 'products')}`];
  if (s.joining) parts.push(`${s.joining} ${plural(s.joining, 'joins', 'join')} as additional IDs`);
  if (s.splitOff) parts.push(`${s.splitOff} split off`);
  if (s.leftOut) parts.push(`${s.leftOut} left out (additional IDs of other tours)`);
  if (s.movingBookings) {
    parts.push(
      `bookings of ${s.movingBookings} ${plural(s.movingBookings, 'product', 'products')} will be moved`,
    );
  }
  return parts.join(' · ');
}
